import api from '../client';

const employerApiBase = '/employer';

export interface Industry {
  uuid: string;
  name: string;
}

export interface Employer {
  uuid: string;
  name: string;
  legal_name?: string | null;
  industry_id?: string | null;
  industry?: Industry | null;
  abn: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  head_office_location: string | null;
  employee_count?: number;
  status?: 'approved' | 'unapproved' | 'rejected' | 'seeded';
  db_status?: string | null;
  rejection_reason?: string | null;
  onboarded_at?: string | null;
  created_at?: string;
}

export interface Employee {
  uuid: string;
  full_name: string;
  email: string;
  phone: string | null;
  job_title: string | null;
  department: string | null;
  location: string | null;
}

export interface Statistics {
  total_employees: number;
  total_donations: number;
  total_donation_hours: number;
  pending_donations: number;
  awaiting_registration_donations?: number;
  total_payouts: number;
  pending_payouts: number;
}

export interface EmployeesData {
  data: Employee[];
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export interface EmployerProfileData {
  employer: Employer;
  statistics: Statistics;
  employees: EmployeesData;
}

export interface EmployerProfileResponse {
  success: boolean;
  message: string;
  data: EmployerProfileData;
}

export class EmployerProfileService {
  private static readonly baseUrl = '/admin/employer';

  static async getEmployerProfile(
    employerUuid: string,
    employeesPage: number = 1,
    employeesPerPage: number = 20,
    employeesSearch: string = ''
  ): Promise<EmployerProfileResponse> {
    try {
      const params: any = {
        employees_page: employeesPage,
        employees_per_page: employeesPerPage
      };
      
      if (employeesSearch && employeesSearch.trim()) {
        params.employees_search = employeesSearch.trim();
      }
      
      const response = await api.get<EmployerProfileResponse>(
        `${this.baseUrl}/${employerUuid}/profile`,
        { params }
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        throw new Error('Authentication required');
      }
      if (error.response?.status === 403) {
        throw new Error('User is not authorized to access admin employer data');
      }
      if (error.response?.status === 404) {
        throw new Error('Employer not found');
      }
      throw new Error(error.response?.data?.message || 'Failed to retrieve employer profile data');
    }
  }

  static formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2
    }).format(value);
  }

  static formatDate(date: string | null): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-AU');
  }

  /**
   * Reject an employer (mark as rejected so they can be removed from the system).
   * Optionally provide a reason; it will be stored and sent to the user who nominated them.
   */
  static async rejectEmployer(uuid: string, rejectionReason?: string | null): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>(
      `${employerApiBase}/${uuid}/reject`,
      rejectionReason != null && rejectionReason.trim() !== '' ? { rejection_reason: rejectionReason.trim() } : undefined
    );
    return response.data;
  }

  /**
   * Delete an employer (soft delete). Typically used after rejecting.
   */
  static async deleteEmployer(uuid: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete<{ success: boolean; message: string }>(
      `${employerApiBase}/${uuid}`
    );
    return response.data;
  }

  static async updateEmployerProfile(
    uuid: string,
    data: Partial<Pick<Employer, 'name' | 'legal_name' | 'contact_name' | 'contact_email' | 'contact_phone' | 'head_office_location' | 'abn'>>
  ): Promise<{ success: boolean; message: string; data?: Employer }> {
    const response = await api.put<{ success: boolean; message: string; data: Employer }>(
      `${EmployerProfileService.baseUrl}/${uuid}/profile`,
      data
    );
    return response.data;
  }

  static async sendProfileCompletionLink(
    uuid: string,
    data: {
      contact_name: string;
      contact_email: string;
      contact_phone?: string;
      name?: string;
      legal_name?: string;
      head_office_location?: string;
      reason_for_request?: string;
    }
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>(
      `${EmployerProfileService.baseUrl}/${uuid}/send-profile-link`,
      data
    );
    return response.data;
  }

  static async mergeEmployers(
    sourceUuid: string,
    targetUuid: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>(
      '/admin/employers/merge',
      { source_uuid: sourceUuid, target_uuid: targetUuid }
    );
    return response.data;
  }

  static async reassignDonationEmployer(
    donationUuid: string,
    employerUuid: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.put<{ success: boolean; message: string }>(
      `/admin/donation-request/${donationUuid}/employer`,
      { employer_uuid: employerUuid }
    );
    return response.data;
  }
}

export const employerProfileService = EmployerProfileService;

